import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrdersSubTabs from "./OrdersSubTabs";
import OrderMobileCardShell from "./OrderMobileCardShell";
import { matchesPairsDropdown, matchesSideNorm } from "./orderFilterHelpers";
import AuthService from "../../../api/services/AuthService";
import {
  extractOptionsPositionHistoryList,
  formatOptionsPositionHistorySide,
  mapOptionsPositionHistoryRowView,
} from "../options/optionsDataHelpers";
import OrderTablePagination, { useOrderTablePagination } from "./OrderTablePagination";

function extractPositionHistory(res) {
  const fromHelper = extractOptionsPositionHistoryList(res);
  if (fromHelper.length) return fromHelper;
  if (!res || res.success === false) return [];
  const d = res.data;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.history)) return d.history;
  if (Array.isArray(res.items)) return res.items;
  if (Array.isArray(res.history)) return res.history;
  return [];
}

const ORDER_DESKTOP_COL_COUNT = 7;

const CONTRACT_OPTIONS = [
  { value: "all", label: "All" },
  { value: "btc_usdt", label: "BTC/USDT" },
  { value: "eth_usdt", label: "ETH/USDT" },
  { value: "bnb_usdt", label: "BNB/USDT" },
];

const SIDE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "long", label: "Long" },
  { value: "short", label: "Short" },
];

const OptionsPositionHistory = () => {
  const [contract, setContract] = useState("all");
  const [side, setSide] = useState("all");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const res = await AuthService.optionsPositionHistory({
        skip: 0,
        limit: 100,
      });
      setRows(extractPositionHistory(res));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-so-dropdown]")) setOpenDropdown(null);
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const underlying = row.underlying || String(row.symbol || "").split("-")[0];
      const pairLabel = `${underlying}/USDT`;
      if (!matchesPairsDropdown(pairLabel, contract)) return false;
      const sideFmt = formatOptionsPositionHistorySide(row.side);
      if (!matchesSideNorm(sideFmt.label, side)) return false;
      return true;
    });
  }, [rows, contract, side]);

  const { page, setPage, totalPages, pagedRows } = useOrderTablePagination(filteredRows);

  const resetFilters = () => {
    setContract("all");
    setSide("all");
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    return (
    <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter options position history">
      <div className="so-toolbar__left">
        <CustomDropdown
          id={`${pref}oph-dd-contract`}
          label="Contract"
          value={contract}
          options={CONTRACT_OPTIONS}
          onChange={setContract}
          menuKey={`${pref}oph-contract`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
        <CustomDropdown
          id={`${pref}oph-dd-side`}
          label="Side"
          value={side}
          options={SIDE_OPTIONS}
          onChange={setSide}
          menuKey={`${pref}oph-side`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
      </div>
    </div>
    );
  };

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={7} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
        </div>
      </td>
    </tr>
  );

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap so-optionsPositionHistoryPage">
          <main className="so-page" aria-labelledby="so-options-position-history-title">
            <h1 id="so-options-position-history-title" className="so-page__title">
              Position History
            </h1>

            <OrdersSubTabs
              tabs={[
                { label: "Open Orders", to: "/user_profile/orders/options_open_orders" },
                { label: "Order History", to: "/user_profile/orders/options_order_history" },
                { label: "Trade History", to: "/user_profile/orders/options_trade_history" },
                { label: "Position History", to: "/user_profile/orders/options_position_history" },
              ]}
            />

            {renderToolbar(false, "desk")}

            <div className="so-tableWrap so-tableWrap--staking so-optionsPositionHistoryTableShell">
              <table className="so-table so-table--rowLines so-table--air so-table--optionsPositionHistory">
                <colgroup>
                  <col className="so-optionsPositionHistory__cw so-optionsPositionHistory__cw--time" />
                  <col className="so-optionsPositionHistory__cw so-optionsPositionHistory__cw--symbol" />
                  <col className="so-optionsPositionHistory__cw so-optionsPositionHistory__cw--pnl" />
                  <col className="so-optionsPositionHistory__cw so-optionsPositionHistory__cw--source" />
                  <col className="so-optionsPositionHistory__cw so-optionsPositionHistory__cw--side" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">Time</th>
                    <th scope="col">Symbol</th>
                    <th scope="col">Side</th>
                    <th scope="col">Size</th>
                    <th scope="col">Avg Price</th>
                    <th scope="col">Realized PnL</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="so-table__emptyWrap text-center text-muted py-4">
                        Loading…
                      </td>
                    </tr>
                  ) : filteredRows.length === 0
                    ? renderEmptyBody()
                    : pagedRows.map((row) => {
                        const view = mapOptionsPositionHistoryRowView(row);
                        return (
                        <tr key={view.key}>
                          <td className="so-num so-optionsPositionHistory__cellTime">{view.closedAt}</td>
                          <td>{view.symbol}</td>
                          <td>
                            <span className={view.side.className}>{view.side.label}</span>
                          </td>
                          <td className="so-num" title={view.sizeTitle}>{view.size}</td>
                          <td className="so-num">{view.avgPrice}</td>
                          <td>
                            <span className={`so-num ${view.pnl.className}`}>{view.pnl.text}</span>
                          </td>
                          <td className={view.status.className}>{view.status.label}</td>
                        </tr>
                      );})}
                </tbody>
              </table>
            </div>

            <OrderTablePagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              label="Position history pages"
            />
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Options Position History</h2>
          <OrdersSubTabs
            tabs={[
              { label: "Open Orders", to: "/user_profile/orders/options_open_orders" },
              { label: "Order History", to: "/user_profile/orders/options_order_history" },
              { label: "Trade History", to: "/user_profile/orders/options_trade_history" },
              { label: "Position History", to: "/user_profile/orders/options_position_history" },
            ]}
          />
          <div className="so-mobile__toolbar">
            <SoMobileFilters
              modalId="mob-options-position-history-filters"
              toolbar={renderToolbar(true, "mob")}
              onReset={resetFilters}
            />
          </div>
          {loading ? (
            <div className="text-center text-muted py-4">Loading…</div>
          ) : filteredRows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
            </div>
          ) : (
            pagedRows.map((row) => {
              const view = mapOptionsPositionHistoryRowView(row);
              return (
              <OrderMobileCardShell
                key={`m-${view.key}`}
                desktopColumnCount={ORDER_DESKTOP_COL_COUNT}
                popupTitle="Options position details"
                heroPrimary={view.symbol}
                heroSecondary={`${view.side.label} · ${view.status.label}`}
              >
                <div className="so-mobileCard__row">
                  <span>Time</span>
                  <span className="so-num">{view.closedAt}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Size</span>
                  <span className="so-num" title={view.sizeTitle}>{view.size}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Avg Price</span>
                  <span className="so-num">{view.avgPrice}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Open Notional</span>
                  <span>{view.openNotional}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Realized PnL</span>
                  <span className={`so-num ${view.pnl.className}`}>{view.pnl.text}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Status</span>
                  <span className={view.status.className}>{view.status.label}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Side</span>
                  <span className={view.side.className}>{view.side.label}</span>
                </div>
              </OrderMobileCardShell>
            );})
          )}

          <OrderTablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            label="Position history pages"
          />
        </div>
      </div>
    </div>
  );
};

export default OptionsPositionHistory;
